import React, { useEffect } from "react";

// ── Popup informativo reutilizavel ─────────────────────────────────────────
// Componente generico para qualquer necessidade de "informar o cliente" de
// forma elegante e animada: bloqueios de feature/plano, avisos, novidades,
// confirmacoes, etc. Nao tem nenhuma logica de negocio dentro -- so recebe
// conteudo via props e cuida de abrir/fechar/animar.
//
// Uso tipico:
// <InfoPopup
//   open={mostrar}
//   onClose={() => setMostrar(false)}
//   icon="✨"
//   eyebrow="Recurso Premium"
//   title="Checklist de Vistoria"
//   description="Registre a entrada do veiculo com fotos, assinatura do cliente e PDF automatico."
//   beneficios={["Menos disputas sobre avarias antigas", "Cliente assina direto na tela", "PDF pronto pra imprimir/whatsapp"]}
//   acoes={[
//     { label: "Liberar por R$ 40/mes", onClick: () => {...}, variante: "primaria" },
//     { label: "Ver plano Premium", onClick: () => {...}, variante: "secundaria" },
//     { label: "Agora nao", onClick: fechar, variante: "fantasma" },
//   ]}
// />
export default function InfoPopup({
  open,
  onClose,
  icon,
  eyebrow,
  title,
  description,
  beneficios,
  acoes,
  corDestaque = "#4338ca",
  corDestaque2 = "#0ea5e9",
  fecharAoClicarFora = true,
  children,
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape" && onClose) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      onClick={fecharAoClicarFora ? onClose : undefined}
      style={{
        alignItems: "center", background: "#05070dcc", backdropFilter: "blur(2px)",
        bottom: 0, display: "flex", justifyContent: "center", left: 0,
        padding: 16, position: "fixed", right: 0, top: 0, zIndex: 10000,
        animation: "infoPopupFade 0.22s ease both",
      }}
    >
      <style>{`
        @keyframes infoPopupFade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes infoPopupPop { from { opacity: 0; transform: translateY(14px) scale(0.96); } to { opacity: 1; transform: translateY(0) scale(1); } }
        @keyframes infoPopupGlow { 0%,100% { opacity: 0.55; } 50% { opacity: 1; } }
      `}</style>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#131929", border: "1px solid #2a3348", borderRadius: 18,
          boxShadow: "0 24px 64px #000a", maxWidth: 440, padding: "36px 32px 28px",
          position: "relative", textAlign: "center", width: "100%",
          animation: "infoPopupPop 0.32s cubic-bezier(0.16,1,0.3,1) both", overflow: "hidden",
        }}
      >
        <div
          aria-hidden="true"
          style={{
            background: `radial-gradient(circle, ${corDestaque}55, transparent 70%)`,
            borderRadius: "50%", filter: "blur(10px)", height: 180, left: "50%",
            position: "absolute", top: -90, transform: "translateX(-50%)", width: 180,
            zIndex: 0, animation: "infoPopupGlow 3.2s ease-in-out infinite",
          }}
        />

        {onClose && (
          <button
            onClick={onClose}
            aria-label="Fechar"
            style={{
              background: "transparent", border: "none", color: "#64748b", cursor: "pointer",
              fontSize: 18, lineHeight: 1, padding: 6, position: "absolute", right: 10, top: 10, zIndex: 1,
            }}
          >
            ✕
          </button>
        )}

        <div style={{ position: "relative", zIndex: 1 }}>
          {icon && <div style={{ fontSize: 44, marginBottom: 10 }}>{icon}</div>}
          {eyebrow && (
            <div style={{
              color: corDestaque2, fontSize: 11, fontWeight: 800, letterSpacing: "0.08em",
              marginBottom: 6, textTransform: "uppercase",
            }}>
              {eyebrow}
            </div>
          )}
          {title && <div style={{ color: "#f1f5f9", fontSize: 21, fontWeight: 800, marginBottom: 8 }}>{title}</div>}
          {description && <div style={{ color: "#94a3b8", fontSize: 14, lineHeight: 1.5, marginBottom: beneficios?.length ? 18 : 22 }}>{description}</div>}

          {Array.isArray(beneficios) && beneficios.length > 0 && (
            <div style={{
              background: "#0f172a", border: "1px solid #1f2940", borderRadius: 12,
              margin: "0 0 22px", padding: "14px 16px", textAlign: "left",
            }}>
              {beneficios.map((b, i) => (
                <div key={i} style={{ alignItems: "flex-start", color: "#cbd5e1", display: "flex", fontSize: 13, gap: 9, padding: "5px 0" }}>
                  <span style={{ color: "#22c55e", flexShrink: 0, fontWeight: 800, marginTop: 1 }}>✓</span>
                  <span>{b}</span>
                </div>
              ))}
            </div>
          )}

          {children}

          {Array.isArray(acoes) && acoes.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {acoes.map((a, i) => {
                const variante = a.variante || "secundaria";
                const estiloBase = {
                  border: "none", borderRadius: 9, cursor: "pointer", fontSize: 13.5,
                  fontWeight: 700, padding: "12px 18px", width: "100%",
                };
                const estilos = {
                  primaria: { ...estiloBase, background: `linear-gradient(120deg,${corDestaque},${corDestaque2})`, color: "#fff", boxShadow: `0 8px 22px ${corDestaque}55` },
                  secundaria: { ...estiloBase, background: "#1a2035", border: "1px solid #2a3348", color: "#e2e8f0" },
                  fantasma: { ...estiloBase, background: "transparent", color: "#64748b", fontWeight: 600, padding: "6px 18px" },
                };
                return (
                  <button key={i} onClick={a.onClick} style={estilos[variante] || estilos.secundaria}>
                    {a.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
